import type {RevaLanguage} from "../i18n";
import {selectByLanguage} from "../i18n";

/**
 * Der Teil eines AVM-Spec-Eintrags (`locationInfoStandardGroundValue`), den die Anzeige der
 * Bodenrichtwert-Tabelle braucht. Absichtlich strukturell typisiert, damit sowohl der Client
 * als auch der PDF-Service ihre jeweiligen Spec-Objekte ohne Cast hineinreichen können.
 */
export interface GroundValueSpecEntry {
    nameDe?: string | null;
    nameEn?: string | null;
    unitDe?: string | null;
    unitEn?: string | null;
}

/**
 * Anzeigenamen, die REVA abweichend von der AVM-Spec verwendet.
 *
 * Die Spec nennt `flaeche` "Fläche mit Einheit" — ein technischer Name, der aus der Zeit stammt,
 * als das Feld den Rohtext der Gutachterausschüsse inklusive Einheit durchreichen sollte. In den
 * Daten enthalten aber nur 69 von rund 30,5 Mio. Zeilen tatsächlich eine Einheit, und im Gutachten
 * ist ohnehin die Grundstücksgröße des Bodenrichtwertgrundstücks gemeint (#19437).
 */
const GROUND_VALUE_LABEL_OVERRIDES: Record<string, {de: string; en: string}> = {
    flaeche: {de: "Grundstücksgröße", en: "Plot size"},
};

/**
 * Einheiten für Felder, die in der AVM-Spec keine `unitDe`/`unitEn` führen.
 *
 * `flaeche` ist in der Spec ein String (die Gutachterausschüsse liefern hier auch Spannen wie
 * "500-700" oder Staffeln wie "200/350/500") und trägt deshalb keine Einheit. Fachlich ist es
 * immer eine Fläche in m²; das numerische Schwesterfeld `flaeche_m2` führt genau diese Einheit.
 */
const GROUND_VALUE_FALLBACK_UNITS: Record<string, {de: string; en: string}> = {
    flaeche: {de: "m²", en: "m²"},
};

/** Erkennt, ob ein Wert bereits eine Einheit (oder sonstigen Text) trägt, z.B. "800 m²". */
const CONTAINS_UNIT = /[\p{L}²³°]/u;

/**
 * Bodenrichtwert-Felder, die fachlich ganzzahlig sind und deshalb ohne Nachkommastellen
 * dargestellt werden. Die AVM-Spec führt sie als `double`, in der Datenbank sind es Integer —
 * "20,00 m" statt "20 m" wäre eine vorgetäuschte Genauigkeit (#19437).
 */
const GROUND_VALUE_INTEGER_FIELDS = new Set(["grundstueckstiefe", "grundstuecksbreite", "flaeche_m2"]);

/**
 * Ob ein Bodenrichtwert-Feld ohne Nachkommastellen dargestellt wird.
 *
 * @param key Property-Name aus der AVM-Spec
 */
export function isIntegerGroundValueField(key: string): boolean {
    return GROUND_VALUE_INTEGER_FIELDS.has(key);
}

/**
 * Anzeigename eines Bodenrichtwert-Feldes: REVA-Override, sonst der Name aus der AVM-Spec.
 *
 * @param key Property-Name aus der AVM-Spec, z.B. "flaeche"
 * @param specEntry Spec-Eintrag zu diesem Feld
 * @param language UI-Sprache
 * @return der anzuzeigende Name (leerer String, wenn weder Override noch Spec etwas hergeben)
 */
export function groundValueLabel(
    key: string,
    specEntry: GroundValueSpecEntry | null | undefined,
    language: RevaLanguage | null | undefined,
): string {
    const override = GROUND_VALUE_LABEL_OVERRIDES[key];
    if (override) return selectByLanguage(language, override.de, override.en);
    return selectByLanguage(language, specEntry?.nameDe, specEntry?.nameEn) ?? "";
}

/**
 * Einheit eines Bodenrichtwert-Feldes: aus der AVM-Spec, sonst aus den Fallbacks.
 *
 * @return die Einheit oder `null`, wenn das Feld keine trägt
 */
export function groundValueUnit(
    key: string,
    specEntry: GroundValueSpecEntry | null | undefined,
    language: RevaLanguage | null | undefined,
): string | null {
    const fromSpec = selectByLanguage(language, specEntry?.unitDe, specEntry?.unitEn);
    if (fromSpec) return fromSpec;
    const fallback = GROUND_VALUE_FALLBACK_UNITS[key];
    return fallback ? selectByLanguage(language, fallback.de, fallback.en) : null;
}

/**
 * Hängt die Einheit eines Bodenrichtwert-Feldes an den bereits formatierten Wert an (#19437).
 *
 * Idempotent: Werte, die schon eine Einheit tragen (die Gutachterausschüsse liefern vereinzelt
 * "800 m²"), bleiben unverändert — sonst entstünde "800 m² m²". Leere Werte und Platzhalter
 * ("-") bleiben ebenfalls unangetastet.
 *
 * @param key Property-Name aus der AVM-Spec, z.B. "grundstueckstiefe"
 * @param formattedValue der fertig formatierte Wert, so wie er ohne Einheit angezeigt würde
 * @param specEntry Spec-Eintrag zu diesem Feld
 * @param language UI-Sprache
 * @return der Wert mit Einheit
 */
export function withGroundValueUnit(
    key: string,
    formattedValue: string | number | null | undefined,
    specEntry: GroundValueSpecEntry | null | undefined,
    language: RevaLanguage | null | undefined,
): string {
    if (formattedValue === null || formattedValue === undefined) return "";
    const text = String(formattedValue).trim();
    if (text === "" || text === "-") return text;
    if (CONTAINS_UNIT.test(text)) return text;

    const unit = groundValueUnit(key, specEntry, language);
    return unit ? `${text} ${unit}` : text;
}
